// # Shared Dashboard Header Component
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BsBell } from "react-icons/bs"; // # Import bell icon
import { MdLightMode, MdDarkMode } from "react-icons/md"; // # Import theme icons
import {
  FiLogOut,
  FiAlertCircle,
  FiHome,
  FiUsers,
  FiPackage,
  FiBook,
  FiSettings,
  FiSearch,
} from "react-icons/fi"; // # Import menu icons
import { BiCoffee, BiTestTube, BiNotepad, BiCard } from "react-icons/bi"; // # Import quick links icons
import { auth } from "../auth/firebase";
import { useAuthStore } from "../auth/state";
import loginOutManager from "../auth/login_out_manager"; // # Import login manager
import Loading from "./common/Loading"; // # Import Loading component
import AdminNotificationMenu from "../systems/notifications/components/adm_notif_menu";
import UserNotificationMenu from "../systems/notifications/components/usr_notif_menu";
import LibrarySearch from "../search/lib_search"; // # Import search component
import "./DashboardHeader.css";
import sessionManager from "../auth/session";
import notificationManager from "../auth/sessions/notification_session";
import PropTypes from "prop-types"; // # Import PropTypes

function DashboardHeader({ isDarkMode, onThemeToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, resetAuth, isAdmin } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false); // # Add loading state for sign out
  const [isQuickLinksOpen, setIsQuickLinksOpen] = useState(false); // # Add state for quick links menu
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false); // # Add notification menu state
  const [notificationCount, setNotificationCount] = useState(0);
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false); // # Add search state

  // # Check if we're on a dashboard page
  const isDashboard =
    location.pathname === "/dashboard" ||
    location.pathname === "/admin" ||
    location.pathname === "/tutor-dashboard" || // # Specific employee dashboard paths
    location.pathname === "/content-reviewer-dashboard" ||
    location.pathname === "/financial-manager-dashboard" ||
    location.pathname === "/designer-dashboard" ||
    location.pathname === "/support-dashboard" ||
    location.pathname.includes("/roles/employees"); // # Also check for any paths in the employees directory

  // # Handle menu toggle
  const toggleMenu = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  }, []);

  // # Handle quick links toggle
  const toggleQuickLinks = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsQuickLinksOpen((prev) => !prev);
  }, []);

  // # Handle notification menu toggle
  const toggleNotificationMenu = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsNotificationMenuOpen((prev) => !prev);
  }, []);

  // # Handle search toggle
  const toggleSearch = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsSearchOpen((prev) => !prev);
  }, []);

  // # Handle search close
  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  // # Close menu on navigation
  const handleNavigation = useCallback(() => {
    setIsMenuOpen(false);
    setIsQuickLinksOpen(false);
    setIsNotificationMenuOpen(false); // # Add notification menu close
    setIsSearchOpen(false); // # Add search close
  }, []);

  // # Handle sign out
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true); // # Start loading
      // # Record sign out activity before signing out
      if (auth.currentUser) {
        await loginOutManager.recordSignOutActivity(auth.currentUser.uid);
      }

      await auth.signOut();
      resetAuth();
      navigate("/signin");
    } catch (error) {
      console.error("Error signing out:", error);
      alert("Error signing out. Please try again.");
    } finally {
      setIsSigningOut(false); // # End loading
    }
  };

  // # Handle logo click
  const handleLogoClick = () => {
    navigate(isAdmin ? "/admin" : "/dashboard");
    handleNavigation();
  };

  // # Handle back button
  const handleBack = () => {
    // # Special handling for chapter notes pages
    if (
      location.pathname.includes("/notes/") &&
      location.pathname.includes("/USMLE")
    ) {
      // # When on chapter notes, go back to notes overview
      navigate(isAdmin ? "/admin/notes" : "/notes");
    }
    // # Special handling for notes overview page (my_notes.js)
    else if (
      location.pathname === "/notes" ||
      location.pathname === "/admin/notes"
    ) {
      // # When on notes overview, go back to dashboard
      navigate(isAdmin ? "/admin" : "/dashboard");
    } else {
      // # Default behavior - go back in history
      navigate(-1);
    }
    handleNavigation();
  };

  // # Close menu on route change
  useEffect(() => {
    handleNavigation();
  }, [location.pathname, handleNavigation]);

  // # Close menu on escape key and click outside
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setIsQuickLinksOpen(false);
        setIsNotificationMenuOpen(false);
        setIsSignOutModalOpen(false);
        setIsSearchOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      const menu = document.querySelector(".usr_dsh_hdr_nav-menu");
      const hamburger = document.querySelector(".usr_dsh_hdr_hamburger");
      const notificationContainer = document.querySelector(
        ".usr_dsh_hdr_notification-container",
      );
      const notificationMenu = document.querySelector(".notif_menu_container");

      if (
        isNotificationMenuOpen &&
        notificationMenu &&
        !notificationMenu.contains(event.target) &&
        !notificationContainer.contains(event.target)
      ) {
        setIsNotificationMenuOpen(false);
      }

      if (
        isMenuOpen &&
        menu &&
        !menu.contains(event.target) &&
        !hamburger.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isMenuOpen,
    isQuickLinksOpen,
    isNotificationMenuOpen,
    isSignOutModalOpen,
  ]);

  // # Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  // # Quick Links data with icons
  const quickLinks = [
    {
      title: "Home",
      path: isAdmin ? "/admin" : "/dashboard",
      icon: <FiHome />,
    },
    {
      title: "Take a Break",
      path: isAdmin ? "/admin/community/break" : "/community/break",
      icon: <BiCoffee />,
    },
    {
      title: "Labs",
      path: isAdmin ? "/admin/labs" : "/labs",
      icon: <BiTestTube />,
    },
    {
      title: "Notes",
      path: isAdmin ? "/admin/notes" : "/notes",
      icon: <BiNotepad />,
    },
    {
      title: "Flashcards",
      path: isAdmin ? "/admin/flashcards" : "/flashcards",
      icon: <BiCard />,
    },
  ];

  useEffect(() => {
    const updateNotificationCount = () => {
      const notificationState = notificationManager.getNotifications();
      if (!notificationState) return;

      let unseenCount = 0;

      if (isAdmin && notificationState.adminNotifications) {
        // # Count unseen subscription form notifications
        const formNotifs =
          notificationState.adminNotifications.subscription_forms || {};
        unseenCount += Object.values(formNotifs).filter((n) => !n.seen).length;

        // # Count unseen subscription notifications
        const subscNotifs =
          notificationState.adminNotifications.subscriptions || {};
        unseenCount += Object.values(subscNotifs).filter((n) => !n.seen).length;

        // # Count unseen feedback notifications
        const feedbackNotifs =
          notificationState.adminNotifications.feedbacks || {};
        unseenCount += Object.values(feedbackNotifs).filter(
          (n) => !n.seen,
        ).length;

        // # Count unseen review notifications
        const reviewNotifs = notificationState.adminNotifications.reviews || {};
        unseenCount += Object.values(reviewNotifs).filter(
          (n) => !n.seen,
        ).length;

        console.log("Admin unseen count:", {
          formCount: Object.values(formNotifs).filter((n) => !n.seen).length,
          subscCount: Object.values(subscNotifs).filter((n) => !n.seen).length,
          feedbackCount: Object.values(feedbackNotifs).filter((n) => !n.seen)
            .length,
          reviewCount: Object.values(reviewNotifs).filter((n) => !n.seen)
            .length,
          total: unseenCount,
        });
      } else {
        // # Regular user notifications
        const userNotifs = notificationState.notifications || {};
        unseenCount += Object.values(userNotifs).filter((n) => !n.seen).length;

        // # Add ending notifications
        const endingNotifs = notificationState.subsc_ending || {};
        unseenCount += Object.values(endingNotifs).filter(
          (n) => !n.seen,
        ).length;

        // # Add meeting notifications
        const meetingNotifs = notificationState.meetings || {};
        unseenCount += Object.values(meetingNotifs).filter(
          (n) => !n.seen,
        ).length;
      }

      setNotificationCount(unseenCount);
    };

    // # Initial count
    updateNotificationCount();

    // # Setup listener for notification changes
    const handleNotificationUpdate = () => {
      updateNotificationCount();
    };

    // # Subscribe to notification updates
    window.addEventListener("notification_update", handleNotificationUpdate);

    // # Cleanup
    return () => {
      window.removeEventListener(
        "notification_update",
        handleNotificationUpdate,
      );
    };
  }, [isAdmin]);

  // # Handle theme toggle
  const handleThemeToggle = useCallback(() => {
    const newTheme = !isDarkMode;

    // # Call the provided theme toggle handler
    if (onThemeToggle) {
      onThemeToggle(newTheme);
    }

    // # Dispatch theme change event for footer and other components
    const themeChangeEvent = new CustomEvent("theme_change", {
      detail: { isDark: newTheme },
    });
    window.dispatchEvent(themeChangeEvent);
  }, [isDarkMode, onThemeToggle]);

  // # Add helper function for profile photo
  const getProfilePhotoUrl = (userProfile) => {
    try {
      if (!userProfile?.photoURL) return "/icons/pfp.png";
      if (userProfile.photoURL === "") return "/icons/pfp.png";

      // Check if it's a full URL (from Storage)
      if (userProfile.photoURL.startsWith("http")) {
        return userProfile.photoURL;
      }

      // If it's just a filename, assume it's the default
      return "/icons/pfp.png";
    } catch (error) {
      console.error("Error getting profile photo:", error);
      return "/icons/pfp.png";
    }
  };

  // # Handle modal open
  const openSignOutModal = (e) => {
    if (e) e.stopPropagation();
    setIsSignOutModalOpen(true);
  };

  // # Handle modal close
  const closeSignOutModal = (e) => {
    if (e) e.stopPropagation();
    setIsSignOutModalOpen(false);
  };

  // # Handle confirm sign out
  const handleConfirmSignOut = async (e) => {
    if (e) e.stopPropagation();
    closeSignOutModal();
    await handleSignOut();
  };

  return (
    <>
      {isSigningOut && <Loading message="Signing out..." />}

      {/* # Sign Out Confirmation Modal */}
      <div
        className={`usr_dsh_hdr_modal-overlay ${isSignOutModalOpen ? "open" : ""}`}
        onClick={closeSignOutModal}
      >
        <div className="usr_dsh_hdr_modal" onClick={(e) => e.stopPropagation()}>
          <div className="usr_dsh_hdr_modal-header">
            <FiAlertCircle className="usr_dsh_hdr_modal-icon" />
            <h3 className="usr_dsh_hdr_modal-title">Sign Out Confirmation</h3>
          </div>
          <div className="usr_dsh_hdr_modal-content">
            Are you sure you want to sign out? You will need to sign in again to
            access your account.
          </div>
          <div className="usr_dsh_hdr_modal-actions">
            <button
              className="usr_dsh_hdr_modal-button cancel"
              onClick={closeSignOutModal}
            >
              Cancel
            </button>
            <button
              className="usr_dsh_hdr_modal-button confirm"
              onClick={handleConfirmSignOut}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <header
        className={`usr_dsh_hdr_dashboard-header ${isDashboard ? "dashboard" : "page"} ${isDarkMode ? "usr_dsh_hdr_dark-mode" : ""}`}
      >
        <div className="usr_dsh_hdr_header-content">
          {/* # Back button (only on pages) */}
          {!isDashboard && (
            <button
              className="usr_dsh_hdr_back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* # Brand section */}
          <div
            className={`usr_dsh_hdr_brand-section ${isDashboard ? "" : "centered"}`}
            onClick={handleLogoClick}
            style={{ cursor: "pointer" }}
          >
            <div className="usr_dsh_hdr_logo">
              <img
                src="/icons/TEMPU_Logo.png"
                alt="TEMP-U Logo"
                className="usr_dsh_hdr_header-logo"
              />
            </div>
            {isDashboard && (
              <div className="usr_dsh_hdr_title">
                <span className="usr_dsh_hdr_title-short">
                  Welcome, {userProfile?.firstName || "User"}{" "}
                  {isAdmin ? "(Admin)" : ""}
                </span>
              </div>
            )}
          </div>

          {/* # Search Button */}
          <button
            className="usr_dsh_hdr_search-button"
            onClick={toggleSearch}
            aria-label="Search library"
          >
            <FiSearch className="usr_dsh_hdr_search-icon" />
            <span className="usr_dsh_hdr_search-text">Search</span>
          </button>

          {/* # Notification Icon and Menu */}
          <div
            className="usr_dsh_hdr_notification-container"
            onClick={toggleNotificationMenu}
          >
            <div className="usr_dsh_hdr_notification-wrapper">
              <BsBell className="usr_dsh_hdr_notification-icon" />
              {notificationCount > 0 && (
                <span className="usr_dsh_hdr_notification-badge">
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </div>
            {isAdmin ? (
              <AdminNotificationMenu isOpen={isNotificationMenuOpen} />
            ) : (
              <UserNotificationMenu isOpen={isNotificationMenuOpen} />
            )}
          </div>

          {/* # Hamburger menu button */}
          <button
            className={`usr_dsh_hdr_hamburger ${isMenuOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="usr_dsh_hdr_hamburger-icon"></span>
          </button>

          {/* # Menu overlay */}
          <div
            className={`usr_dsh_hdr_menu-overlay ${isMenuOpen ? "open" : ""}`}
            onClick={handleNavigation}
            aria-hidden="true"
          />

          {/* # Navigation menu */}
          <nav
            className={`usr_dsh_hdr_nav-menu ${isMenuOpen ? "open" : ""}`}
            aria-label="Main navigation"
          >
            {/* # Profile Header Section */}
            <div className="usr_dsh_hdr_profile-header">
              <div className="usr_dsh_hdr_profile-info">
                <img
                  src={getProfilePhotoUrl(userProfile)}
                  alt="Profile"
                  className="usr_dsh_hdr_profile-avatar"
                />
                <div className="usr_dsh_hdr_profile-name-container">
                  <div className="usr_dsh_hdr_profile-name">
                    {userProfile?.firstName && userProfile?.lastName
                      ? `${userProfile.firstName} ${userProfile.lastName}`
                      : userProfile?.email?.split("@")[0] || "User"}
                  </div>
                  <button
                    onClick={openSignOutModal}
                    className="usr_dsh_hdr_signout-icon"
                    aria-label="Sign out"
                  >
                    <FiLogOut />
                  </button>
                </div>
              </div>
            </div>

            {/* # Theme Toggle Section */}
            <button
              className="usr_dsh_hdr_theme-toggle"
              onClick={handleThemeToggle}
              aria-label={
                isDarkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              <div className="usr_dsh_hdr_theme-content">
                <div className="usr_dsh_hdr_theme-icon-wrapper">
                  {isDarkMode ? (
                    <MdLightMode className="usr_dsh_hdr_theme-icon light" />
                  ) : (
                    <MdDarkMode className="usr_dsh_hdr_theme-icon dark" />
                  )}
                </div>
                <span className="usr_dsh_hdr_theme-label">
                  {isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                </span>
              </div>
            </button>

            {/* # Scrollable Content Area */}
            <div className="usr_dsh_hdr_nav-menu-content">
              <ul>
                <li>
                  <Link
                    to={isAdmin ? "/admin" : "/dashboard"}
                    onClick={handleNavigation}
                  >
                    <FiHome className="usr_dsh_hdr_menu-icon" /> Home
                  </Link>
                </li>
                <li>
                  <Link to="/team" onClick={handleNavigation}>
                    <FiUsers className="usr_dsh_hdr_menu-icon" /> Meet Our Team
                  </Link>
                </li>
                <li>
                  <Link to="/products" onClick={handleNavigation}>
                    <FiPackage className="usr_dsh_hdr_menu-icon" /> Our Products
                  </Link>
                </li>
                <li>
                  <Link to="/samples" onClick={handleNavigation}>
                    <FiBook className="usr_dsh_hdr_menu-icon" /> Library Samples
                  </Link>
                </li>
                <li>
                  <Link
                    to={isAdmin ? "/admin/settings" : "/settings"}
                    onClick={handleNavigation}
                  >
                    <FiSettings className="usr_dsh_hdr_menu-icon" /> Settings
                  </Link>
                </li>

                {/* # Quick Links Section */}
                <li className="usr_dsh_hdr_quick-links-section">
                  <button
                    className="usr_dsh_hdr_quick-links-toggle"
                    onClick={toggleQuickLinks}
                    aria-expanded={isQuickLinksOpen}
                  >
                    <span>Quick Links (New Tab)</span>
                    <svg
                      className={`usr_dsh_hdr_quick-links-arrow ${isQuickLinksOpen ? "open" : ""}`}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div
                    className={`usr_dsh_hdr_quick-links-content ${isQuickLinksOpen ? "open" : ""}`}
                  >
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="usr_dsh_hdr_quick-link"
                      >
                        {link.icon}
                        <span>{link.title}</span>
                      </a>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </nav>
        </div>
      </header>

      {/* # Search Component */}
      <LibrarySearch
        isOpen={isSearchOpen}
        onClose={handleSearchClose}
        isDarkMode={isDarkMode}
      />
    </>
  );
}

DashboardHeader.propTypes = {
  isDarkMode: PropTypes.bool,
  onThemeToggle: PropTypes.func,
};

export default DashboardHeader;
